import * as React from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { BarChart3 } from 'lucide-react'
import { analyticsSearchSchema } from './-components/analytics-search-schema'
import { AnalyticsFiltersCard } from './-components/AnalyticsFiltersCard'
import { KpiMetricSelector } from './-components/KpiMetricSelector'
import { MeetingsPanel } from './-components/MeetingsPanel'
import { OrphanedTicketsPanel } from './-components/OrphanedTicketsPanel'
import { EpOutcomesPanel } from './-components/EpOutcomesPanel'
import { PulledBackToDataEntryButton } from './-components/PulledBackToDataEntryButton'
import { AnnualReviewsCard } from './-components/AnnualReviewsCard'
import {
  SPECIALIST_VIEW_HEADINGS,
  specialistKpiQueriesEnabled,
} from './-components/specialist-view'
import { OverviewTab } from './-components/OverviewTab'
import { PeopleTab } from './-components/PeopleTab'
import { ServiceQueueLink } from './-components/ServiceQueueLink'
import { ProductivityTrendCard } from './-components/ProductivityTrendCard'
import { SectionHeading } from './-components/SectionHeading'
import { SpecialistPeopleTab } from './-components/SpecialistPeopleTab'
import {
  ANALYTICS_DEPARTMENT_TABS,
  analyticsVisibleDepartments,
} from './-components/analytics-department-tabs'
import type { SpecialistView } from './-components/specialist-view'
import type { KpiMetricEntry } from './-components/KpiMetricSelector'
import type {
  AnalyticsSavedViewFilters,
  AnalyticsSavedViewRecord,
  AnalyticsTab,
  SpecialistTimeGrouping,
} from '@/types/backofficeAnalytics.types'
import { PageTitle } from '@/components/PageTitle'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/animate-ui/components/radix/tabs'
import { SaveViewDialog } from '@/components/shared/SaveViewDialog'
import {
  isAnalyticsRefreshing,
  useBackofficeAnalyticsAlternativeScenariosCompleted,
  useBackofficeAnalyticsAlternativeScenariosRequested,
  useBackofficeAnalyticsCasesOpen,
  useBackofficeAnalyticsChangeRequests,
  useBackofficeAnalyticsErrorsAddressed,
  useBackofficeAnalyticsInformationResolved,
  useBackofficeAnalyticsNewPlansWorked,
  useBackofficeAnalyticsOverview,
  useBackofficeAnalyticsPeople,
  useBackofficeAnalyticsPlanWork,
  useBackofficeAnalyticsProductivity,
  useBackofficeAnalyticsSpecialistPeople,
  useBackofficeAnalyticsSpecialistTime,
  useResyncTickets,
} from '@/hooks/repositories/useBackofficeAnalyticsRepository'
import { useAnalyticsFilters } from '@/hooks/use-cases/useAnalyticsFilters'
import { useAnalyticsViews } from '@/hooks/use-cases/useAnalyticsViews'
import { formatDateOnlyShort, formatDateTime } from '@/lib/formatters'
import { requirePermission } from '@/lib/permissions/route-guards'
import { useCan } from '@/hooks/permissions/use-can'
import { useDepartment } from '@/hooks/permissions/use-department'
import { BACKOFFICE_PERMISSION_KEYS } from '@/lib/permissions/backoffice-permissions'
import { SyncProgressButton } from '@/components/shared/SyncProgressButton'
import { queryKeys } from '@/lib/query-client'
import {
  getSyncBatch,
  getTicketsSyncStatus,
} from '@/api/services/hubspot.service'

export const Route = createFileRoute('/_app/backoffice/analytics/')({
  component: RouteComponent,
  validateSearch: analyticsSearchSchema,
  // One grant reaches the page; the caller's own department decides what is on
  // it. Deliberately NOT a department check here — that would bounce every
  // non-ADMIN, sending a specialist who was granted the console back to /home.
  beforeLoad: requirePermission(BACKOFFICE_PERMISSION_KEYS.analytics),
})

function RouteComponent() {
  // The console's whole current search state — carried through verbatim to
  // the Service Queue link to the Forecasts sub-route (sc-1212) so its own back-link returns
  // here with exactly the filters/tab the reader left, rather than a
  // hardcoded default.
  const search = Route.useSearch()
  const {
    filters,
    tab,
    preset,
    setFilters,
    setRangePreset,
    setCustomRange,
    setTab,
    resetFilters,
    hasActiveFilters,
    peopleSort,
    setPeopleSort,
    activeViewId,
    applyView,
    setSavedView,
  } = useAnalyticsFilters()

  const {
    views,
    defaultTab: defaultViewId,
    saveView,
    updateView,
    renameView,
    setViewShared,
    deleteView,
    toggleDefault,
    setDefaultTab,
    getViewById,
  } = useAnalyticsViews()

  // Captures exactly the page's current filters — see
  // `AnalyticsSavedViewFilters`'s doc comment for why `person` is included
  // (it's a real current filter, whoever set it) and `selectedMetricKey`
  // isn't (it's UI state that never reaches `filters` at all).
  const currentViewFilters: AnalyticsSavedViewFilters = filters

  // Applies the default saved view once, only when the page loaded with no
  // active filter — mirrors CheckoutsTab's identical effect. `replace: true`
  // (inside `applyView`) keeps it out of the back-button history.
  const appliedDefaultViewRef = React.useRef(false)
  React.useEffect(() => {
    if (appliedDefaultViewRef.current) return
    appliedDefaultViewRef.current = true
    if (!defaultViewId) return
    const defaultView = getViewById(defaultViewId)
    if (!defaultView) return
    // `hasActiveFilters` already covers every field a saved view can carry
    // (experience/department/person/preset) — reusing it here rather than
    // re-deriving the same check keeps the two checks from drifting apart.
    if (hasActiveFilters) return
    applyView(defaultView.filters, defaultView.id)
    // Deliberately not depending on `hasActiveFilters`/`applyView`: this
    // effect is guarded to run once, on mount, by the ref above —
    // re-running it on every filters change would re-apply the default view
    // over a since-changed filter.
  }, [defaultViewId, getViewById])

  const [saveViewDialogOpen, setSaveViewDialogOpen] = React.useState(false)
  const [viewBeingUpdated, setViewBeingUpdated] =
    React.useState<AnalyticsSavedViewRecord | null>(null)

  const handleSaveNewView = () => {
    setViewBeingUpdated(null)
    setSaveViewDialogOpen(true)
  }

  const handleUpdateViewRequest = (view: AnalyticsSavedViewRecord) => {
    setViewBeingUpdated(view)
    setSaveViewDialogOpen(true)
  }

  const handleSaveView = (
    name: string,
    isDefault: boolean,
    isShared: boolean,
  ) => {
    if (viewBeingUpdated) {
      updateView(viewBeingUpdated.id, currentViewFilters)
      if (name !== viewBeingUpdated.name) renameView(viewBeingUpdated.id, name)
      if (isShared !== viewBeingUpdated.isShared) {
        setViewShared(viewBeingUpdated.id, isShared)
      }
      // Set explicitly rather than `toggleDefault` — toggling would UN-default
      // an already-default view re-saved with the switch on, since toggle
      // flips off defaultTab === id rather than asserting the intended state.
      if (isDefault) setDefaultTab(viewBeingUpdated.id)
      setSavedView(viewBeingUpdated.id)
    } else {
      saveView(name, currentViewFilters, undefined, isDefault, isShared)
    }
  }

  const handleDeleteView = (viewId: string) => {
    deleteView(viewId)
    if (activeViewId === viewId) setSavedView(undefined)
  }

  const { has } = useCan()
  const { department } = useDepartment()
  // Null means "every department", which is also what qualifies someone for the
  // firm-wide Overview tab — it is everyone's numbers, so seeing it is the same
  // question.
  const visibleDepartments = analyticsVisibleDepartments(department, has)
  const canSeeFirmWide = visibleDepartments === null
  const departmentTabs = ANALYTICS_DEPARTMENT_TABS.filter(
    (candidate) =>
      canSeeFirmWide || visibleDepartments.includes(candidate.department),
  )
  // Land on a tab this person can actually open. `tab` defaults to `overview`,
  // which is not a specialist's to see, so for them the two differ on arrival.
  const effectiveTab = React.useMemo(() => {
    if (tab === 'overview') {
      return canSeeFirmWide ? tab : (departmentTabs[0]?.value ?? tab)
    }
    return departmentTabs.some((candidate) => candidate.value === tab)
      ? tab
      : (departmentTabs[0]?.value ?? 'overview')
  }, [tab, canSeeFirmWide, departmentTabs])

  // Derived from `effectiveTab`, NEVER from the raw `tab`.
  //
  // Everything below keys off this: which filters the Planners query sends, and
  // `showProductivity`, which enables the Specialists-tab queries. Read
  // from `tab` instead, a specialist arriving at the default `?tab=overview`
  // was SHOWN the Specialists tab — `effectiveTab` had corrected it — while
  // every query on it stayed disabled, so the tab rendered completely empty.
  // Anyone who can see Overview never hits it, because for them the two agree.
  const activeDepartmentTab = departmentTabs.find(
    (candidate) => candidate.value === effectiveTab,
  )

  const overview = useBackofficeAnalyticsOverview(filters, {
    enabled: canSeeFirmWide,
  })
  // A department tab is the People table narrowed to one tier. The server
  // clamps this to what the caller may read regardless of what is sent, so the
  // filter here is for the view, not for the access control.
  const peopleFilters = React.useMemo(
    () =>
      activeDepartmentTab
        ? { ...filters, department: [activeDepartmentTab.department] }
        : filters,
    [filters, activeDepartmentTab],
  )
  // KPI 1 rides on the department tabs: it is a Case Design metric, and Case
  // Design is what specialists do. Only fetched when such a tab is open.
  // KPI 1 is a CASE DESIGN metric, and Case Design is specialist work — so it
  // belongs on the Specialists tab only, not on every department tab. Planners
  // do not move these tickets, and a card of zeroes on their tab would read as
  // a performance problem rather than an irrelevant metric.
  const showProductivity = activeDepartmentTab?.value === 'specialists'
  // Which of the three views the Specialists section is showing (sc-1212),
  // driven by the toggle buttons next to the Person picker. Only offered on
  // that tab — Case Design is specialist work, same gating
  // `showProductivity`/`resyncAction` already use. Same "UI state, not a
  // filter, not in the URL" treatment as `selectedMetricKey` below: nothing
  // here is worth a share link over reopening it, and it doesn't narrow
  // `filters` or count toward `hasActiveFilters`/"Clear filters".
  //
  // Declared up here, beside `showProductivity`, because the KPI queries below
  // are gated on it: the Unowned and Plan Delivery views replace the KPI tiles
  // outright, and a query behind a tile that is not on screen would otherwise
  // keep polling every minute for nobody (sc-1620).
  const [specialistView, setSpecialistView] =
    React.useState<SpecialistView>('productivity')
  const showSpecialistKpis = specialistKpiQueriesEnabled(
    showProductivity,
    specialistView,
  )
  // Only the Planners tab renders this table now. Enabled whenever a
  // non-Specialists department tab is open; Overview and the empty state do not
  // need it (the firm-wide People tab that did is gone).
  const people = useBackofficeAnalyticsPeople(peopleFilters, {
    enabled: activeDepartmentTab !== undefined && !showProductivity,
  })
  // The Specialists tab's own throughput table (forecasts created / escape
  // plans generated / Plan Work) — replaces the firm-wide `PeopleTab` there
  // (sc-1212 follow-up). Plain `filters`, not `peopleFilters`: the endpoint
  // is already scoped to whatever the caller's department grants allow
  // server-side, same as the other department-tab KPI queries below.
  // "Compare to previous period" (sc-1212 month-over-month follow-up) —
  // default OFF: it doubles the query's work, so nobody pays for it without
  // asking. UI state, not a filter/URL param — same "nothing here is worth a
  // share link" posture `selectedMetricKey`/`specialistView` already use, and
  // it's specific to this one table, not a page-wide comparison mode.
  const [compareEnabled, setCompareEnabled] = React.useState(false)
  const specialistPeople = useBackofficeAnalyticsSpecialistPeople(filters, {
    enabled: showProductivity,
    compare: compareEnabled,
  })
  const productivity = useBackofficeAnalyticsProductivity(filters, {
    enabled: showSpecialistKpis,
  })
  // KPI 2 — new plans worked. Same gating as KPI 1: a Case Design /
  // specialist metric, only fetched when that tab is open.
  const newPlansWorked = useBackofficeAnalyticsNewPlansWorked(filters, {
    enabled: showSpecialistKpis,
  })
  // Case Lifecycle (sc-1212, stage three; renamed from "Time with
  // Specialist" in a later UX pass — see the tile's `infoHint` for why: this
  // sums every Case Design stage but Ready/Closed/Refund Review, which is
  // the case's whole lifecycle up to the plan being ready, not specialist
  // effort — production's 23.6-day median is 21.1 days of RC Data Entry
  // alone). `groupBy` lives here, not inside the tile/panel, because the
  // tile's headline (`overallMedianMs`/`ticketsConsidered`) must stay
  // populated regardless of which grouping was last requested or which tile
  // is currently selected. Person is the default grouping (§9b: "who is
  // slow" is the more common starting question on this tab).
  const [specialistTimeGroupBy, setSpecialistTimeGroupBy] =
    React.useState<SpecialistTimeGrouping>('person')
  const specialistTime = useBackofficeAnalyticsSpecialistTime(
    specialistTimeGroupBy,
    filters,
    { enabled: showSpecialistKpis },
  )
  // The other half of the Case Lifecycle card (sc-1212 round 3 correction,
  // restored — see `SpecialistTimeDetailBody`'s doc comment): the same
  // cases measured as elapsed rather than worked, sharing Case Lifecycle's
  // Person/Month toggle.
  const planWork = useBackofficeAnalyticsPlanWork(
    specialistTimeGroupBy,
    filters,
    { enabled: showSpecialistKpis },
  )
  // Cases Ready's Plan Work medians + first-delivery/re-delivery split
  // (sc-1212 round 3 correction) — one query backs both: `casesReady`
  // (first-delivery/re-delivery) and `firstPassMedianMs`/`reworkMedianMs`/
  // `reworkSharePct` (the "New work"/"Revisions" group stacked beside the
  // headline, per Tyler's layout — see `ChangeRequestRollup`'s doc comment).
  // Lifted to the route, same reasoning as every other tile's query: Cases
  // Ready's headline must stay populated regardless of selection.
  const changeRequests = useBackofficeAnalyticsChangeRequests(filters, {
    enabled: showSpecialistKpis,
  })
  // "Reopened after close" (sc-1212, Tyler 2026-10-01) — `changeRequests.data
  // .kinds[].reopened` is per `CHANGE_REQUEST_FLOW_KINDS` key
  // (`alternativeScenarios`/`revisions`/`additionalInfo`), not per tile, so
  // each of the three tiles below looks its own kind up by that key rather
  // than assuming array position.
  const reopenedByKind = React.useMemo(
    () =>
      new Map(
        (changeRequests.data?.kinds ?? []).map((kind) => [
          kind.key,
          { count: kind.reopened, raised: kind.raised },
        ]),
      ),
    [changeRequests.data],
  )
  // Open/Ready split (sc-1212, Tyler 2026-10-01) — added to the Cases Ready
  // tile without touching anything already on it. Own query, not folded into
  // `changeRequests`: `CasesOpenLevel` is a LEVEL (see its doc comment),
  // nothing else on this tab is, and mixing a level into a flow-shaped
  // endpoint would make that distinction easy to lose server-side too.
  const casesOpen = useBackofficeAnalyticsCasesOpen(filters, {
    enabled: showSpecialistKpis,
  })
  // Alt scenarios (sc-1212 round 3 correction) — requested and completed,
  // merged into one tile (Tyler: "we merged Alt scen requested and
  // completed cause they go together to read better in 1 rollup"). Two
  // separate KPI queries, not the change-requests rollup — simpler for this
  // one pairing (see `AltScenariosDetailBody`'s doc comment).
  const alternativeScenariosRequested =
    useBackofficeAnalyticsAlternativeScenariosRequested(filters, {
      enabled: showSpecialistKpis,
    })
  const alternativeScenariosCompleted =
    useBackofficeAnalyticsAlternativeScenariosCompleted(filters, {
      enabled: showSpecialistKpis,
    })
  // Errors addressed, split by change source (sc-1212 round 3 correction,
  // RESTORED — a round-3 pass had merged this into the "Change requests"
  // tile as a drill-in, which cost the `change_source` breakdown its own
  // chart. Tyler's correction kept only the Alt scenarios merge; Errors
  // addressed goes back to being its own tile.
  const errorsAddressed = useBackofficeAnalyticsErrorsAddressed(filters, {
    enabled: showSpecialistKpis,
  })
  // Information resolved, split by missing_information (sc-1212 round 3
  // correction, RESTORED — same reasoning as `errorsAddressed` above).
  const informationResolved = useBackofficeAnalyticsInformationResolved(
    filters,
    { enabled: showSpecialistKpis },
  )

  // The "By person" drill-in (sc-1212): clicking a row REPLACES
  // `filters.person` with just that one id, scoping the WHOLE page — every
  // metric in `KpiMetricSelector` plus the People table below — to that one
  // person, even if the toolbar's `UserPicker` currently holds a
  // multi-person selection.
  // Clicking the same, already-sole selection clears it instead, rather than
  // sending the manager to the toolbar to undo it. This is deliberately a
  // single-person drill-in, not "add to the current multi-select" — from a
  // card, "who is this" is a one-person question.
  const togglePersonFilter = React.useCallback(
    (userId: string) => {
      const isSoleSelection =
        filters.person.length === 1 && filters.person[0] === userId
      setFilters({ person: isSoleSelection ? [] : [userId] })
    },
    [filters.person, setFilters],
  )

  // Which metric tile drives the shared chart+people panel below the
  // selector row. UI state, not a filter — deliberately NOT in the URL
  // (unlike `peopleSort`, this has no cross-page-load value worth a share
  // link over just defaulting back to the first card) — see §9.
  const [selectedMetricKey, setSelectedMetricKey] = React.useState('casesMoved')

  const specialistMetrics = React.useMemo<Array<KpiMetricEntry>>(
    () => [
      {
        kind: 'productivity',
        key: 'casesMoved',
        // Row one, left — one of the selector's two "detail" tiles (sc-1212
        // round 4 two-row redesign). See `ProductivityMetricEntry.emphasis`.
        emphasis: 'detail',
        // Renamed from "Cases moved" (sc-1212): the API now excludes cases
        // sent back to rework from this count, so the tile is a claim about
        // what's ready NOW, not a historical tally of every move. The
        // `casesMoved` key is unchanged — it's the API contract and the
        // records `metric` param, not user-facing copy.
        title: 'Cases Ready',
        // "310 Ready" — the headline's label, matching the Open group's
        // "after the number" convention (sc-1212 round 4 labelling fix). See
        // `ProductivityMetricEntry.headlineLabel`'s doc comment.
        headlineLabel: 'Ready',
        infoHint: {
          label: 'What this counts',
          children: (
            <>
              Cases that reached{' '}
              {productivity.data?.stageLabel ?? 'Ready for Presentation'} and
              are still there, or left by finishing — Closed or Refund Review
              Sent. A case sent back for rework (Alternative Scenarios,
              Additional Information or Revisions) is excluded: it isn't ready.
              Attributed to whoever owned the ticket when it arrived.
              <br />
              <br />
              Open is every Case Design ticket not Closed or Refund Review Sent
              — a level standing right now, not a count inside this
              window&rsquo;s range. See the Case Lifecycle tile for how long a
              case takes and how much of that is Plan Work versus queue time.
            </>
          ),
        },
        data: productivity.data,
        isPending: isAnalyticsRefreshing(productivity),
        // `isLoadingError`, not `isError`, for every polled query in this
        // memo: a failed background poll leaves `isError` true while the
        // last good numbers are still cached, and the tile would swap them
        // for "Couldn't load" until the next poll (sc-1620).
        isError: productivity.isLoadingError,
        onRetry: () => void productivity.refetch(),
        seriesLabel: 'Cases Ready',
        chartColorVar: '--chart-1',
        emptyActivityLabel: 'cases reached',
        emptyNoHistoryLabel:
          'No ticket has reached this stage yet. Resync tickets if you expect some.',
        peopleRankedByLabel: 'cases ready',
        peopleEmptyLabel: 'Nobody had a case reach Ready in this window.',
        recordsMetric: 'casesMoved',
        // First-delivery / re-delivery split (sc-1212 round 3, Tyler's
        // screenshot annotation). Reads `GET /analytics/change-requests`
        // rather than this tile's own endpoint — see `ChangeRequestRollup`'s
        // doc comment.
        casesReadySplit: changeRequests.data?.casesReady,
        // Open/Ready split (sc-1212, Tyler 2026-10-01) — its own
        // isPending/isError rather than folded into the tile's main
        // `isPending`/`isError`: this is additional context on top of Cases
        // Ready, not a dependency of it, so a slow/failed `casesOpen` query
        // must not blank the headline the rest of the tile already has.
        casesOpen: casesOpen.data,
        casesOpenIsPending: isAnalyticsRefreshing(casesOpen),
        casesOpenIsError: casesOpen.isLoadingError,
        onCasesOpenRetry: () => void casesOpen.refetch(),
      },
      // Case Lifecycle. Not `AnalyticsProductivity`-shaped (see
      // `SpecialistTimeMetricEntry`'s doc comment), so it carries its own
      // fields rather than being contorted into the productivity shape —
      // `KpiMetricSelector` picks the right tile/panel rendering off `kind`.
      //
      // Tile title renamed from "Time with Specialist" (sc-1212 UX pass,
      // Tyler) — the old name promised effort and delivered queue time. The
      // metric key (`specialistTime`, below) is unchanged: it's the wire
      // contract for `GET /analytics/specialist-time` and the `metric` param
      // on `GET /analytics/records`, not display text.
      {
        kind: 'specialistTime',
        key: 'specialistTime',
        // Row one, right — the selector's other "detail" tile, pairing with
        // Cases Ready (sc-1212 round 4). See
        // `SpecialistTimeMetricEntry.emphasis`.
        emphasis: 'detail',
        title: 'Case Lifecycle',
        // Static, unlike the productivity tiles whose badge the API generates
        // from their stage ids — this one measures a duration across stages,
        // not a move between two of them, so there is no transition to derive.
        badge: 'All Case Design stages except Ready, Closed, Refund Review',
        infoHint: {
          label: 'What this measures',
          children: (
            <>
              Median time from a case opening to Ready for Presentation,
              excluding Ready for Presentation itself (queued for a meeting),
              Closed and Refund Review Sent — everything else in Case Design, RC
              Data Entry included. This is the case&rsquo;s whole lifecycle, not
              specialist effort: on production, RC Data Entry alone accounts for
              most of the median.
              <br />
              <br />
              &ldquo;Worked&rdquo; is Plan Work&rsquo;s median — the same cases
              measured from the post-data-entry stages only, first-pass builds
              and rework combined. A wide gap between elapsed and worked means
              the case was waiting (mostly RC Data Entry), not that specialists
              were slow.
              <br />
              <br />
              &ldquo;New work&rdquo; and &ldquo;Revisions&rdquo; split that
              worked median into building a plan the first time round versus
              servicing a change to one already delivered. Revisions showing
              &ldquo;None&rdquo; means no case took a change request in this
              window — a good outcome, not missing data. These medians include
              Financial Planning work alongside Rothology — they always have;
              the Specialists table&rsquo;s Financial Planning column breaks
              that load out per person.
              <br />
              <br />
              Both medians count only cases that have FINISHED that half — a
              case still sitting in that stage has a duration still climbing, so
              it is withheld and shown as &ldquo;+N in flight&rdquo; beside the
              median instead of being pooled into it. In flight is not a defect
              count; it is expected load.
            </>
          ),
        },
        description: 'Select a bar to see the cases behind it.',
        chartColorVar: '--chart-2',
        data: specialistTime.data,
        isPending: isAnalyticsRefreshing(specialistTime),
        isError: specialistTime.isLoadingError,
        onRetry: () => void specialistTime.refetch(),
        groupBy: specialistTimeGroupBy,
        onGroupByChange: setSpecialistTimeGroupBy,
        filters,
        // Plan Work's medians (sc-1212 round 4 — moved here FROM the Cases
        // Ready tile, Tyler: "bring in the Plan Work data... just like in
        // Alt scenarios you have the slash and stuff"). Same
        // `GET /analytics/change-requests` payload Cases Ready's
        // `casesReadySplit` above still reads.
        planWorkSummary: changeRequests.data && {
          firstPassMedianMs: changeRequests.data.firstPassMedianMs,
          firstPassSampleSize: changeRequests.data.firstPassSampleSize,
          reworkMedianMs: changeRequests.data.reworkMedianMs,
          reworkSampleSize: changeRequests.data.reworkSampleSize,
          reworkSharePct: changeRequests.data.reworkSharePct,
          firstPassInFlight: changeRequests.data.firstPassInFlight,
          reworkInFlight: changeRequests.data.reworkInFlight,
        },
        // Whether those changes arrived in time to be delivered (sc-1212
        // round 7) — same payload, one more dimension on a card that already
        // measures rework rather than a tile of its own.
        arrivalTiming: changeRequests.data?.arrivalTiming,
      },
      {
        kind: 'productivity',
        key: 'newPlansWorked',
        // Row two — a plain "compact" tile (sc-1212 round 4 two-row
        // redesign, Tyler: "move New plans worked down to the same row as
        // Alt scenarios, Errors addressed and Information resolved"). Not
        // removed, just relocated — see `ProductivityMetricEntry.emphasis`.
        emphasis: 'compact',
        title: 'New plans worked',
        infoHint: {
          label: 'What this counts',
          children: (
            <>
              Cases entering{' '}
              {newPlansWorked.data?.stageLabel ?? 'Planning in Progress'} from
              RC Data Entry or Data Entry Complete. Attributed to whoever owned
              the ticket at the moment it moved.
            </>
          ),
        },
        data: newPlansWorked.data,
        isPending: isAnalyticsRefreshing(newPlansWorked),
        isError: newPlansWorked.isLoadingError,
        onRetry: () => void newPlansWorked.refetch(),
        seriesLabel: 'New plans worked',
        chartColorVar: '--chart-3',
        emptyActivityLabel: 'plans entered',
        emptyNoHistoryLabel: 'No ticket has entered this stage yet.',
        peopleRankedByLabel: 'plans worked',
        peopleEmptyLabel: 'Nobody worked a new plan in this window.',
        recordsMetric: 'newPlansWorked',
      },
      // Alt scenarios (sc-1212 round 3 correction) — requested and
      // completed, merged into one rollup (Tyler, on his screenshot: "we
      // merged Alt scen requested and completed cause they go together to
      // read better in 1 rollup"). Two separate `AnalyticsProductivity`
      // queries, presented together — see `AltScenariosDetailBody`'s doc
      // comment for why this reads the per-KPI hooks rather than the
      // change-requests rollup.
      {
        kind: 'altScenarios',
        key: 'altScenarios',
        emphasis: 'compact',
        title: 'Alt scenarios',
        infoHint: {
          label: 'What this counts',
          children: (
            <>
              Alternative Scenarios requested and completed — the same flow
              counted at both ends. Requested counts a case entering{' '}
              {alternativeScenariosRequested.data?.stageLabel ??
                'Alternative Scenarios Needed'}
              ; completed counts one leaving it for{' '}
              {alternativeScenariosCompleted.data?.stageLabel ??
                'Ready for Presentation'}
              . Attributed to whoever owned the ticket at the moment it moved.
            </>
          ),
        },
        description:
          'Requested is when a planner asks for rework; completed is when it ships. A growing gap between them is a growing backlog, not two unrelated counts.',
        chartColorVar: '--chart-4',
        requested: alternativeScenariosRequested.data,
        completed: alternativeScenariosCompleted.data,
        isPending:
          isAnalyticsRefreshing(alternativeScenariosRequested) ||
          isAnalyticsRefreshing(alternativeScenariosCompleted),
        isError:
          alternativeScenariosRequested.isLoadingError ||
          alternativeScenariosCompleted.isLoadingError,
        onRetry: () => {
          void alternativeScenariosRequested.refetch()
          void alternativeScenariosCompleted.refetch()
        },
        requestedRecordsMetric: 'alternativeScenariosRequested',
        completedRecordsMetric: 'alternativeScenariosCompleted',
        reopened: reopenedByKind.get('alternativeScenarios')?.count,
        // The meeting-true counterpart of `reopened` (sc-1212 round 8) — same
        // payload, shown beside it rather than replacing it.
        afterEscapePlanHeld:
          changeRequests.data?.arrivalTiming.afterEscapePlanHeld,
      },
      // Errors addressed (sc-1212 round 3 correction, RESTORED as its own
      // tile — a prior pass had merged this into a "Change requests" tile as
      // a drill-in, which cost the `change_source` breakdown chart below its
      // own tile. Tyler's correction kept only the Alt scenarios merge.
      {
        kind: 'productivity',
        key: 'errorsAddressed',
        emphasis: 'compact',
        title: 'Errors addressed',
        infoHint: {
          label: 'What this counts',
          children: (
            <>
              Cases leaving Revisions Needed for either{' '}
              {errorsAddressed.data?.stageLabel ??
                'Planning in Progress or Ready for Presentation'}{' '}
              — the revision has been dealt with either way. Switch to the Pie
              tab below to see the split by whose error caused it: Client,
              Planning Team or Specialist Team, plus Other and Not set when
              non-zero. Most tickets state no cause, so Not set is usually the
              largest slice — it&rsquo;s kept rather than hidden so the chart
              still sums to the total above it. Attributed to whoever owned the
              ticket at the moment it moved.
            </>
          ),
        },
        data: errorsAddressed.data,
        isPending: isAnalyticsRefreshing(errorsAddressed),
        isError: errorsAddressed.isLoadingError,
        onRetry: () => void errorsAddressed.refetch(),
        seriesLabel: 'Errors addressed',
        chartColorVar: '--chart-5',
        emptyActivityLabel: 'revisions addressed',
        emptyNoHistoryLabel: 'No ticket has left this stage yet.',
        peopleRankedByLabel: 'errors addressed',
        peopleEmptyLabel: 'Nobody addressed a revision in this window.',
        recordsMetric: 'errorsAddressed',
        reopened: reopenedByKind.get('revisions'),
      },
      // Information resolved (sc-1212 round 3 correction, RESTORED as its
      // own tile — same reasoning as Errors addressed above; this one's
      // `missing_information` breakdown is `breakdownKind: 'multi'`, so it
      // renders as the "By category" item list, not a pie (§9d).
      {
        kind: 'productivity',
        key: 'informationResolved',
        emphasis: 'compact',
        title: 'Information resolved',
        infoHint: {
          label: 'What this counts',
          children: (
            <>
              Cases leaving Additional Information Needed for{' '}
              {informationResolved.data?.stageLabel ??
                'Planning in Progress, Revisions Needed or Ready for Presentation'}{' '}
              — whatever it was waiting on has arrived, wherever it goes next.
              Switch to the By category tab below to see WHAT was missing: Sales
              Notes, Estate Planning &amp; Beneficiary, Long-Term Care &amp;
              Major Expenses, Withdrawal Sequencing, Tax Planning &amp;
              Payments, Investment Alignment or Roth Conversion Strategy notes.
            </>
          ),
        },
        description: (
          <>
            The total above counts CASES leaving that stage. The By category
            breakdown counts ITEMS flagged on <code>missing_information</code> —
            a HubSpot checkbox, so one case can flag several — and does not sum
            to the total.
          </>
        ),
        data: informationResolved.data,
        isPending: isAnalyticsRefreshing(informationResolved),
        isError: informationResolved.isLoadingError,
        onRetry: () => void informationResolved.refetch(),
        seriesLabel: 'Information resolved',
        chartColorVar: '--chart-6',
        emptyActivityLabel: 'cases unblocked from',
        emptyNoHistoryLabel:
          'No ticket has left Additional Information Needed yet.',
        peopleRankedByLabel: 'information resolved',
        peopleEmptyLabel: 'Nobody resolved a blocker in this window.',
        recordsMetric: 'informationResolved',
        reopened: reopenedByKind.get('additionalInfo'),
      },
    ],
    [
      productivity.data,
      productivity.isPending,
      productivity.isLoadingError,
      productivity.refetch,
      newPlansWorked.data,
      newPlansWorked.isPending,
      newPlansWorked.isLoadingError,
      newPlansWorked.refetch,
      specialistTime.data,
      specialistTime.isPending,
      specialistTime.isLoadingError,
      specialistTime.refetch,
      specialistTimeGroupBy,
      changeRequests.data,
      changeRequests.isPending,
      changeRequests.isLoadingError,
      changeRequests.refetch,
      reopenedByKind,
      casesOpen.data,
      casesOpen.isPending,
      casesOpen.isPlaceholderData,
      casesOpen.isLoadingError,
      casesOpen.refetch,
      alternativeScenariosRequested.data,
      alternativeScenariosRequested.isPending,
      alternativeScenariosRequested.isLoadingError,
      alternativeScenariosRequested.refetch,
      alternativeScenariosCompleted.data,
      alternativeScenariosCompleted.isPending,
      alternativeScenariosCompleted.isLoadingError,
      alternativeScenariosCompleted.refetch,
      errorsAddressed.data,
      errorsAddressed.isPending,
      errorsAddressed.isLoadingError,
      errorsAddressed.refetch,
      informationResolved.data,
      informationResolved.isPending,
      informationResolved.isLoadingError,
      informationResolved.refetch,
      filters,
    ],
  )

  // Whichever tile `selectedMetricKey` currently names, or the first tile as
  // a fallback (same "clamp to a valid index" precedent `KpiMetricSelector`
  // itself follows internally) — lifted up here, not read out of
  // `KpiMetricSelector`, because `ProductivityTrendCard` below it needs to
  // know the same selection without `KpiMetricSelector` exposing internal
  // state.
  const selectedMetric =
    specialistMetrics.find((metric) => metric.key === selectedMetricKey) ??
    specialistMetrics[0]

  const resync = useResyncTickets()
  const queryClient = useQueryClient()
  const canImportTickets = has(BACKOFFICE_PERMISSION_KEYS.hubspot)
  const resyncButton = canImportTickets ? (
    <SyncProgressButton
      label="Resync tickets"
      entityLabel="tickets"
      batchId={resync.data?.batchId ?? null}
      isStarting={resync.isPending}
      onStart={() => resync.mutate()}
      // Own key, not `queryKeys.hubspot.syncBatch` — this widget's poll is a
      // UI concern of this page's KPI cards, not the integrations page's
      // sync-batch cache.
      queryKeyBase={[...queryKeys.backoffice.analytics.all, 'ticket-sync']}
      fetchBatch={getSyncBatch}
      fetchLatestStatus={getTicketsSyncStatus}
      // The whole point of the button: refresh the numbers once the batch
      // that just ran has actually finished, not the instant it was enqueued.
      onSynced={() => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.backoffice.analytics.all,
        })
      }}
    />
  ) : undefined

  return (
    <div className="space-y-4 py-4">
      <PageTitle title="Internal Reporting and KPIs" />

      {/* One instance for the whole page, not per `AnalyticsFiltersCard` —
          the dialog operates on whichever view a manager just clicked
          "Save"/"Update" on in the currently-visible tab's toolbar. */}
      <SaveViewDialog
        open={saveViewDialogOpen}
        onOpenChange={setSaveViewDialogOpen}
        onSave={handleSaveView}
        existingName={viewBeingUpdated?.name}
        existingIsShared={viewBeingUpdated?.isShared}
        allowSharing
        description="Saves the current filters as a reusable view."
      />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex size-8 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <BarChart3 className="size-5" aria-hidden />
          </div>
          <div>
            {/* Smaller than it was: at text-3xl the title tied with every stat
                value on the page, so nothing was the top of the hierarchy. */}
            <h1 className="text-xl font-bold tracking-tight sm:text-2xl">
              Analytics
            </h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Revenue, pipeline and per-person throughput across the firm.
            </p>
          </div>
        </div>

        {/* The API has always returned `generatedAt` and `window`; neither was
            rendered, so every figure on the page was undated. A number with no
            window and no timestamp is an assertion, not a measurement. */}
        {overview.data && (
          <div className="text-xs text-muted-foreground sm:text-right">
            <p className="tabular-nums">
              {formatDateOnlyShort(overview.data.window.from)} —{' '}
              {formatDateOnlyShort(overview.data.window.to)}
            </p>
            <p className="tabular-nums">
              Updated {formatDateTime(overview.data.generatedAt)}
            </p>
          </div>
        )}
      </div>

      <Tabs
        value={effectiveTab}
        onValueChange={(value) => setTab(value as AnalyticsTab)}
      >
        {/* The Service Queue link sits BESIDE the tablist, not in it: it
            navigates to another page, so it is a link, not a tab. */}
        <div className="flex flex-wrap items-center gap-1">
          <TabsList className="flex-wrap">
            {/* Overview carries firm-wide revenue, so it stays behind the
                firm-wide check. A departmental grant opens only its own tab. */}
            {canSeeFirmWide && (
              <TabsTrigger value="overview">Overview</TabsTrigger>
            )}
            {departmentTabs.map((departmentTab) => (
              <TabsTrigger
                key={departmentTab.value}
                value={departmentTab.value}
              >
                {departmentTab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <ServiceQueueLink search={search} />
        </div>

        {/* A caller whose own department has no tab (ADVISOR, now that the
            Advisors tab is gone) is not firm-wide either, so they have neither
            Overview nor a department tab. Say so instead of rendering a blank
            console. Visibility is unchanged: this is a missing door, not a
            permission. */}
        {!canSeeFirmWide && departmentTabs.length === 0 && (
          <div
            role="status"
            className="text-muted-foreground mt-4 flex items-center gap-2 rounded-lg border border-dashed p-4 text-sm"
          >
            <BarChart3 className="size-4 shrink-0" aria-hidden />
            <p>
              The analytics console has nothing to show for your department.
            </p>
          </div>
        )}

        {/* Below the tabs, not above them. The Person picker only applies to
            the department tabs, so mounting the card above made it grow a
            fourth column and reflow the page every time the tab changed. */}
        {canSeeFirmWide && (
          <TabsContent value="overview" className="mt-4 space-y-4">
            <AnalyticsFiltersCard
              filters={filters}
              preset={preset}
              showPersonFilter={false}
              hasActiveFilters={hasActiveFilters}
              onChange={setFilters}
              onRangePresetChange={setRangePreset}
              onCustomRangeChange={setCustomRange}
              onReset={resetFilters}
              views={views}
              activeViewId={activeViewId}
              defaultViewId={defaultViewId}
              onSelectView={(view) => applyView(view.filters, view.id)}
              onSaveNewView={handleSaveNewView}
              onUpdateView={handleUpdateViewRequest}
              onToggleDefaultView={toggleDefault}
              onDeleteView={handleDeleteView}
            />
            <OverviewTab
              data={overview.data}
              isPending={overview.isPending}
              isError={overview.isError}
              filters={filters}
              onRetry={() => void overview.refetch()}
            />
          </TabsContent>
        )}

        {departmentTabs.map((departmentTab) => (
          <TabsContent
            key={departmentTab.value}
            value={departmentTab.value}
            className="mt-4 space-y-4"
          >
            {/* `hideDepartment`: the tab IS the department, so offering the
                filter would let somebody narrow a Specialists tab to Advisors
                and get an empty table. */}
            <AnalyticsFiltersCard
              filters={filters}
              preset={preset}
              showPersonFilter
              hasActiveFilters={hasActiveFilters}
              onChange={setFilters}
              onRangePresetChange={setRangePreset}
              onCustomRangeChange={setCustomRange}
              onReset={resetFilters}
              hideDepartment
              // The import writes against HubSpot's API, so it is gated on
              // the integrations permission rather than on analytics access
              // — reading the numbers and rebuilding them from HubSpot are
              // different privileges. Only Specialists had a resync button
              // before (it lived in the "Cases moved" card header) — keep it
              // there, don't add it to tabs that never had it.
              resyncAction={
                departmentTab.value === 'specialists' ? resyncButton : undefined
              }
              // Case Design — and therefore its unowned backlog — is
              // specialist work, same gating `showProductivity`/
              // `resyncAction` already use for this tab specifically.
              showOrphanedToggle={departmentTab.value === 'specialists'}
              specialistView={
                departmentTab.value === 'specialists'
                  ? specialistView
                  : 'productivity'
              }
              onSpecialistViewChange={setSpecialistView}
              pulledBackAction={
                departmentTab.value === 'specialists' ? (
                  <PulledBackToDataEntryButton filters={filters} />
                ) : undefined
              }
              showCompareToggle={departmentTab.value === 'specialists'}
              compareEnabled={compareEnabled}
              onCompareChange={setCompareEnabled}
              views={views}
              activeViewId={activeViewId}
              defaultViewId={defaultViewId}
              onSelectView={(view) => applyView(view.filters, view.id)}
              onSaveNewView={handleSaveNewView}
              onUpdateView={handleUpdateViewRequest}
              onToggleDefaultView={toggleDefault}
              onDeleteView={handleDeleteView}
            />
            {departmentTab.value === 'specialists' && (
              <>
                <SectionHeading>{departmentTab.label}</SectionHeading>
                {/* Leads the Specialists tab deliberately — this is the
                    tab's own throughput table (forecasts created / escape
                    plans generated / Plan Work), so it comes first, above
                    the charts and Meetings below. The shared `PeopleTab`
                    columns (client counts, data gathering, annual reviews,
                    checkouts) are firm-wide, not specialist throughput, and
                    don't answer what this tab is for. Every other
                    department tab keeps `PeopleTab` exactly as it was, at
                    the bottom of its tab. */}
                <SpecialistPeopleTab
                  people={specialistPeople.data?.rows}
                  isPending={isAnalyticsRefreshing(specialistPeople)}
                  isError={specialistPeople.isLoadingError}
                  filters={filters}
                  sort={peopleSort}
                  onSortChange={setPeopleSort}
                  hasActiveFilters={hasActiveFilters}
                  onClearFilters={resetFilters}
                  onRetry={() => void specialistPeople.refetch()}
                  isPartialWindow={specialistPeople.data?.isPartialWindow}
                />

                <SectionHeading>
                  {SPECIALIST_VIEW_HEADINGS[specialistView]}
                </SectionHeading>
                {/* The toggle SWAPS the section, it doesn't add to it — each
                    answers a different question ("who's behind on their own
                    work" / "what has nobody claimed at all") and showing
                    more than one at once would mean unrelated charts under
                    one heading. "Pulled back to data entry" is deliberately
                    NOT a third view here (sc-1212 round 3 correction) — it's
                    a filter button in the toolbar above
                    (`pulledBackAction`), not a section swap. See
                    `OrphanedTicketsPanel`'s doc comment for why these two
                    remain sibling panels rather than more `KpiMetricSelector`
                    tiles. */}
                {specialistView === 'orphaned' ? (
                  <OrphanedTicketsPanel filters={filters} />
                ) : specialistView === 'epOutcomes' ? (
                  <EpOutcomesPanel filters={filters} />
                ) : (
                  <>
                    <KpiMetricSelector
                      metrics={specialistMetrics}
                      planWork={{
                        data: planWork.data,
                        isPending: isAnalyticsRefreshing(planWork),
                        isError: planWork.isLoadingError,
                        onRetry: () => void planWork.refetch(),
                        // `--chart-7`, not Case Lifecycle's own `--chart-2`
                        // — a second, distinct hue beside it so the two
                        // panes of the same card read as different series
                        // rather than the same one measured twice (§1).
                        // `--chart-7`/`--chart-8` are unassigned to any tile
                        // in the current six-tile ramp, so this doesn't
                        // collide with a tile's own accent.
                        chartColorVar: '--chart-7',
                      }}
                      selectedKey={selectedMetricKey}
                      onSelectedKeyChange={setSelectedMetricKey}
                      selectedPersonIds={filters.person}
                      onPersonFilterToggle={togglePersonFilter}
                      filters={filters}
                    />
                    {/* Only a `ProductivityMetricEntry` has a daily `count`
                        series to smooth — `SpecialistTimeMetricEntry`
                        (durations) and `AltScenariosMetricEntry` (two
                        counts, no single daily series to smooth) have
                        nothing this chart could honestly plot, so the trend
                        card simply doesn't mount for them, same as the
                        Orphaned-backlog toggle above already excluding
                        non-productivity metrics (§12 in
                        ANALYTICS_DESIGN_STANDARD.md). */}
                    {selectedMetric.kind === 'productivity' && (
                      <ProductivityTrendCard
                        metric={selectedMetric}
                        selectedPersonIds={filters.person}
                      />
                    )}
                  </>
                )}

                {/* Its own card, not folded into the toggle above or gated
                    by it — a standing firm-wide snapshot (sc-1212), same
                    posture as Meetings below it, unrelated to which of
                    productivity/orphaned/epOutcomes is currently selected.
                    Placed between the charts and Meetings per the brief. */}
                <AnnualReviewsCard />

                {/* Its own section, not folded into the toggle above — the
                    productivity/orphaned swap answers "who's behind" or
                    "what's unowned"; Meetings answers a third, unrelated
                    question ("what's on the calendar"), so it stays visible
                    regardless of which of those two views is active. */}
                <SectionHeading>Meetings</SectionHeading>
                <MeetingsPanel filters={filters} />
              </>
            )}

            {/* Every department tab except Specialists, which has its own
                table above — in practice, Planners. */}
            {departmentTab.value !== 'specialists' && (
              <>
                <SectionHeading>{departmentTab.label}</SectionHeading>
                <PeopleTab
                  people={people.data}
                  isPending={people.isPending}
                  isError={people.isError}
                  sort={peopleSort}
                  onSortChange={setPeopleSort}
                  originTab={departmentTab.value as AnalyticsTab}
                  hasActiveFilters={hasActiveFilters}
                  onClearFilters={resetFilters}
                  onRetry={() => void people.refetch()}
                />
              </>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}
